import React, { useState, useEffect } from 'react';
import { Card, Spin, Modal, Tag, Empty, Pagination, message, Button, Checkbox, Space, Popconfirm, Input, Select } from 'antd';
import api, { ImageSearchResult, getImageThumbnail, getAllTags } from '../services/api';
import { getBaiduTaskStatus } from '../services/api';
import './GalleryPage.css'; // 可以复用大部分样式

const MobileGalleryPage: React.FC = () => {
  const [images, setImages] = useState<ImageSearchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedImage, setSelectedImage] = useState<ImageSearchResult | null>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const pollRef = React.useRef<number | null>(null);
  const [searchText, setSearchText] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [bulkVisible, setBulkVisible] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkUpdateDesc, setBulkUpdateDesc] = useState(false);
  const [bulkDesc, setBulkDesc] = useState('');
  const [bulkUpdateTags, setBulkUpdateTags] = useState(false);
  const [bulkTags, setBulkTags] = useState('');
  const [bulkTagOperation, setBulkTagOperation] = useState<'replace' | 'add' | 'remove'>('replace');
  const [resyncing, setResyncing] = useState(false);

  useEffect(() => {
    const fetchImages = async () => {
      setLoading(true);
      try {
        const params: any = { page: currentPage, page_size: 20, all: true, search: (searchText || undefined) };
        if (selectedTags.length > 0) params.tags = JSON.stringify(selectedTags);
        const { data } = await api.get('/images/', { params });
        setImages((data.images || []) as ImageSearchResult[]);
        const total = data.total || 0;
        setTotalPages(Math.max(1, Math.ceil(total / 20)));
      } catch (error: any) {
        const status = error?.response?.status;
        if (status === 401) {
          message.error('未登录或登录已过期，请先登录');
          window.location.href = '/matching/login';
        } else {
          message.error('图片列表加载失败');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, [currentPage, searchText, selectedTags.join(',')]);

  // 拉取所有可用标签
  useEffect(() => {
    (async () => {
      try {
        const res: any = await getAllTags();
        const list: string[] = Array.isArray(res) ? res : (Array.isArray(res?.tags) ? res.tags : []);
        setAvailableTags(list.filter((t) => typeof t === 'string' && t.trim()).map(t => t.trim()));
      } catch {}
    })();
  }, []);

  // 轮询可见图片的百度同步状态
  useEffect(() => {
    const poll = async () => {
      try {
        const ids = images.map((img: any) => img.id).filter(Boolean);
        if (ids.length === 0) return;
        const res = await getBaiduTaskStatus(ids);
        const statuses = (res && (res as any).statuses) || {} as Record<number, any>;
        if (Object.keys(statuses).length === 0) return;
        setImages(prev => prev.map((img: any) => {
          const st = statuses[img.id];
          if (!st) return img;
          if (st.status === 'done' && st.cont_sign) {
            return { ...img, baidu_synced: true, baidu_sign: st.cont_sign };
          }
          if (st.status === 'error') {
            return { ...img, baidu_synced: false };
          }
          return img;
        }));
      } catch {}
    };
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
    pollRef.current = window.setInterval(poll, 2000);
    return () => {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [images.map((i: any) => i.id).join(','), currentPage]);

  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    setSelectedIds([]);
  };

  const handleSelect = (imageId: number, checked: boolean) => {
    setSelectedIds(prev => checked ? Array.from(new Set([...prev, imageId])) : prev.filter(id => id !== imageId));
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const visible = images.filter((img: any) => {
        if (!Array.isArray(img.tags) || selectedTags.length === 0) return true;
        const imgTags = img.tags.map((t: any) => String(t)).filter(Boolean);
        return selectedTags.every(t => imgTags.includes(t));
      });
      setSelectedIds(visible.map((img: any) => img.id));
    } else {
      setSelectedIds([]);
    }
  };

  const batchDelete = async () => {
    if (selectedIds.length === 0) {
      message.warning('请先选择要删除的图片');
      return;
    }
    try {
      const resp = await api.post('/images/batch-delete', selectedIds, { headers: { 'Content-Type': 'application/json' } });
      if (resp.status >= 200 && resp.status < 300) {
        message.success('已提交删除');
        setSelectedIds([]);
        setSelectMode(false);
        // 重新加载当前页
        const { data } = await api.get('/images/', { params: { page: currentPage, page_size: 20 } });
        setImages((data.images || []) as ImageSearchResult[]);
        const total = data.total || 0;
        setTotalPages(Math.max(1, Math.ceil(total / 20)));
      } else {
        message.error('删除失败');
      }
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      message.error(detail || '删除失败，请重试');
    }
  };

  // 批量重同步到百度
  const batchResyncBaidu = async () => {
    if (selectedIds.length === 0) {
      message.warning('请先选择要重同步的图片');
      return;
    }
    setResyncing(true);
    try {
      const resp = await api.post('/images/baidu/resync', selectedIds, { headers: { 'Content-Type': 'application/json' } });
      if (resp.status >= 200 && resp.status < 300) {
        const data = resp.data || {} as any;
        message.success(`已重同步 ${data.updated ?? selectedIds.length} 张，失败 ${data.error_count ?? 0} 张`);
        setSelectedIds([]);
        setSelectMode(false);
        // 刷新当前页
        try {
          const { data } = await api.get('/images/', { params: { page: currentPage, page_size: 20, all: true, search: (searchText || undefined) } });
          setImages((data.images || []) as ImageSearchResult[]);
          const total = data.total || 0;
          setTotalPages(Math.max(1, Math.ceil(total / 20)));
        } catch {}
      } else {
        message.error('重同步失败');
      }
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      message.error(detail || '重同步失败，请重试');
    } finally {
      setResyncing(false);
    }
  };

  const handleImageClick = (image: ImageSearchResult) => {
    setSelectedImage(image);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><Spin size="large" /></div>;
  }

  return (
    <div className="mobile-gallery-page" style={{ padding: '12px' }}>
      {/* 搜索栏：按名称/描述 + 标签过滤 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder="搜索名称或描述"
          onSearch={(v) => { setCurrentPage(1); setSearchText(v.trim()); }}
          enterButton
        />
        <Select
          mode="tags"
          allowClear
          placeholder="选择或输入标签"
          value={selectedTags}
          onChange={(vals) => setSelectedTags((vals as string[]).map(v => v.trim()).filter(Boolean))}
          options={availableTags.map(t => ({ value: t, label: t }))}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Space>
          <Button size="small" onClick={toggleSelectMode} type={selectMode ? 'primary' : 'default'}>
            {selectMode ? '取消选择' : '批量选择'}
          </Button>
          {selectMode && (
            <Checkbox
              checked={selectedIds.length === images.length && images.length > 0}
              indeterminate={selectedIds.length > 0 && selectedIds.length < images.length}
              onChange={(e) => handleSelectAll(e.target.checked)}
            >
              全选
            </Checkbox>
          )}
        </Space>
        {selectMode && (
          <Space>
            <Popconfirm
              title={`确定删除选中的 ${selectedIds.length} 张图片吗？`}
              onConfirm={batchDelete}
              okText="确定"
              cancelText="取消"
              disabled={selectedIds.length === 0}
            >
              <Button danger size="small" disabled={selectedIds.length === 0}>批量删除</Button>
            </Popconfirm>
            <Button size="small" onClick={batchResyncBaidu} loading={resyncing} disabled={selectedIds.length === 0}>重同步到百度</Button>
            <Button type="primary" size="small" disabled={selectedIds.length === 0} onClick={() => setBulkVisible(true)}>批量修改</Button>
          </Space>
        )}
      </div>
      {/* 标签前端筛选 */}
      {(() => {
        const filtered = images.filter((img: any) => {
          if (!Array.isArray(img.tags) || selectedTags.length === 0) return true;
          const imgTags = img.tags.map((t: any) => String(t)).filter(Boolean);
          return selectedTags.every(t => imgTags.includes(t));
        });
        return (
          filtered.length === 0 ? (
            <Empty description="暂无图片" />
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px', maxWidth: 360, margin: '0 auto' }} className="mobile-gallery-grid">
                {filtered.map((image) => (
                  <Card
                    key={image.id}
                    hoverable
                    cover={
                      <div style={{ position: 'relative' }}>
                        <img src={getImageThumbnail(image.id, 300)} alt={image.filename} style={{ height: '120px', objectFit: 'cover', width: '100%' }} />
                        <div style={{ position: 'absolute', top: 6, right: 6 }}>
                          {((image as any).baidu_synced || (image as any).baidu_sign) ? (
                            <Tag color="success" style={{ padding: '0 6px', fontSize: 10 }}>已入库(百度)</Tag>
                          ) : (
                            <Tag color="default" style={{ padding: '0 6px', fontSize: 10 }}>未入库(百度)</Tag>
                          )}
                        </div>
                      </div>
                    }
                    bodyStyle={{ padding: '8px' }}
                    onClick={() => handleImageClick(image)}
                  >
                    {selectMode && (
                      <Checkbox
                        checked={selectedIds.includes(image.id)}
                        onChange={(e) => handleSelect(image.id, e.target.checked)}
                        style={{ position: 'absolute', top: 8, left: 8 }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                    <Card.Meta
                      title={<span style={{ fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{image.filename}</span>}
                    />
                    {Array.isArray((image as any).tags) && (image as any).tags.length > 0 && (
                      <div style={{ marginTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {(image as any).tags.slice(0, 3).map((t: string, idx: number) => (
                          <Tag key={idx} style={{ marginBottom: 2 }}>{t}</Tag>
                        ))}
                        {((image as any).tags.length > 3) && (
                          <span style={{ fontSize: 11, color: '#999' }}>+{(image as any).tags.length - 3}</span>
                        )}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
              <Pagination
                current={currentPage}
                total={totalPages * 20}
                pageSize={20}
                onChange={(page) => setCurrentPage(page)}
                style={{ marginTop: '24px', textAlign: 'center' }}
                showSizeChanger={false}
                simple
              />
            </>
          )
        );
      })()}

      <Modal
        open={!!selectedImage}
        onCancel={handleCloseModal}
        footer={null}
        title={selectedImage?.filename}
        width="90vw"
      >
        {selectedImage && (
          <div>
            <img src={selectedImage.id ? getImageThumbnail((selectedImage as any).id as number, 800) : `/uploads/${selectedImage.filename}`} alt={selectedImage.filename} style={{ width: '100%', marginBottom: '16px', objectFit: 'contain' }} />
            <p><strong>描述:</strong> {(selectedImage as any).description || '—'}</p>
            <div>
              <strong>标签:</strong>
              <div style={{ marginTop: '8px' }}>
                {Array.isArray((selectedImage as any).tags) && (selectedImage as any).tags.length > 0 ? (
                  (selectedImage as any).tags.map((tag: string) => <Tag key={tag}>{tag}</Tag>)
                ) : (
                  <span style={{ color: '#999' }}>无标签</span>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
      <Modal
        title={`批量修改（${selectedIds.length} 张）`}
        open={bulkVisible}
        onCancel={() => { if (!bulkLoading) { setBulkVisible(false); setBulkUpdateDesc(false); setBulkUpdateTags(false); setBulkDesc(''); setBulkTags(''); setBulkTagOperation('replace'); }}}
        okButtonProps={{ loading: bulkLoading, disabled: selectedIds.length === 0 || (!bulkUpdateDesc && !bulkUpdateTags) }}
        onOk={async () => {
          if (selectedIds.length === 0) { message.warning('请先选择图片'); return; }
          if (!bulkUpdateDesc && !bulkUpdateTags) { message.warning('请选择至少一项修改内容'); return; }
          setBulkLoading(true);
          try {
            const { batchUpdateTags, updateImageMeta } = await import('../services/api');
            if (bulkUpdateTags) {
              const tags = (bulkTags || '').split(',').map(s => s.trim()).filter(Boolean);
              await batchUpdateTags(selectedIds, tags, bulkTagOperation);
            }
            if (bulkUpdateDesc) {
              const desc = (bulkDesc || '').trim();
              for (const id of selectedIds) {
                await updateImageMeta(id, { description: desc });
              }
            }
            message.success('批量修改已提交');
            setBulkVisible(false);
            setBulkUpdateDesc(false);
            setBulkUpdateTags(false);
            setBulkDesc('');
            setBulkTags('');
            setBulkTagOperation('replace');
            setSelectedIds([]);
            setSelectMode(false);
            // refresh
            try {
              const { data } = await api.get('/images/', { params: { page: currentPage, page_size: 20, all: true, search: (searchText || undefined) } });
              setImages((data.images || []) as ImageSearchResult[]);
              const total = data.total || 0;
              setTotalPages(Math.max(1, Math.ceil(total / 20)));
            } catch {}
          } catch (e: any) {
            const detail = e?.response?.data?.detail;
            message.error(detail || '批量修改失败，请重试');
          } finally {
            setBulkLoading(false);
          }
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <Checkbox checked={bulkUpdateDesc} onChange={(e) => setBulkUpdateDesc(e.target.checked)}>更改描述</Checkbox>
            <Input.TextArea rows={3} placeholder="新描述" value={bulkDesc} onChange={(e) => setBulkDesc(e.target.value)} disabled={!bulkUpdateDesc} maxLength={200} />
          </div>
          <div>
            <Checkbox checked={bulkUpdateTags} onChange={(e) => setBulkUpdateTags(e.target.checked)}>更改标签</Checkbox>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
              <select value={bulkTagOperation} onChange={(e) => setBulkTagOperation(e.target.value as any)} disabled={!bulkUpdateTags}>
                <option value="replace">替换</option>
                <option value="add">追加</option>
                <option value="remove">移除</option>
              </select>
              <Input placeholder="多个标签用逗号分隔" value={bulkTags} onChange={(e) => setBulkTags(e.target.value)} disabled={!bulkUpdateTags} />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MobileGalleryPage;
